/**
 * Lógica pura de ordenamiento de la cola de atención.
 * Sin dependencias React — facilita testing con fast-check (PBT).
 *
 * Requirements: M4.1.2
 */

import type { ColaAtencionItem } from "@/hooks/use-muestras";

// =============================================================================
// MAPA DE PRIORIDAD NUMÉRICA
// Sincronizar con PRIORIDAD_ORDEN en backend/apps/muestras/constants.py
// Menor número = mayor prioridad (STAT se muestra primero)
// =============================================================================

export const PRIORIDAD_ORDEN: Record<string, number> = {
  STAT: 0,
  URGENTE: 1,
  NORMAL: 2,
};

// =============================================================================
// FUNCIONES PURAS
// =============================================================================

/**
 * Ordena la cola de atención por prioridad (STAT primero) y hora de ingreso (ASC).
 * Función pura — no muta el array original.
 *
 * Requirements: M4.1.2
 */
export function sortCola(items: ColaAtencionItem[]): ColaAtencionItem[] {
  return [...items].sort((a, b) => {
    const prioridadA = a.prioridad_orden ?? PRIORIDAD_ORDEN[a.prioridad] ?? 99;
    const prioridadB = b.prioridad_orden ?? PRIORIDAD_ORDEN[b.prioridad] ?? 99;

    if (prioridadA !== prioridadB) {
      return prioridadA - prioridadB;
    }

    // Misma prioridad → ordenar por hora de ingreso ASC
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

/**
 * Retorna el color CSS del badge de prioridad.
 * STAT=rojo, URGENTE=naranja, NORMAL=verde
 *
 * Requirements: M4.1.6
 */
export function getColorPrioridad(prioridad: string): string {
  const colores: Record<string, string> = {
    STAT: "text-red-600",
    URGENTE: "text-orange-500",
    NORMAL: "text-green-600",
  };
  return colores[prioridad] ?? "text-gray-500";
}

/**
 * Retorna el variant de Badge para cada prioridad.
 */
export function getBadgeVariantPrioridad(
  prioridad: string
): "default" | "secondary" | "destructive" | "outline" {
  const variantes: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    STAT: "destructive",
    URGENTE: "outline",
    NORMAL: "secondary",
  };
  return variantes[prioridad] ?? "secondary";
}

/**
 * Filtra la cola por tipo de servicio.
 */
export function filtrarPorTipoServicio(
  items: ColaAtencionItem[],
  tipoServicio: string | null
): ColaAtencionItem[] {
  if (!tipoServicio || tipoServicio === "all") return items;
  return items.filter((item) => item.tipo_servicio_derivado === tipoServicio);
}
